// @ts-nocheck

import PropTypes from 'prop-types';
import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { isHexColor } from 'sonata-react-components';
import {
  BaseCurrencyEnum,
  BaseCurrencyPositionEnum,
  BaseCurrencySymbolEnum
} from 'sonata-static-storage/lib/enum/baseCurrency.enum';

import { ReportingInsightsRankingItem } from '@/modules/reporting/ReportingInsights';

function ReportingInsightsItem({
  campaignInfo,
  chartConfig = {},
  chartType,
  containerSize,
  country,
  currencyInfo,
  family,
  field,
  filterOptions,
  filters,
  groupsOptions,
  hideKpi = false,
  hideOthers,
  id,
  insightsDataBeingChecked = false,
  insightsDataCanBeLoaded = true,
  insightsDataTotal,
  itemId,
  metricsKpis,
  onConfigChange,
  onFiltersChange,
  rankingConfig,
  rawDataMode,
  reportOptions,
  section,
  showMoreQuantity,
  shownItems = 5,
  title,
  variant
}) {
  const intl = useIntl();

  const handleConfigChange = useCallback(
    config => {
      onConfigChange?.(family, id, config);
    },
    [family, id, onConfigChange]
  );

  const className = `reportingInsightsItem reportingInsightsItem--${variant} reportingInsightsItem--${containerSize}`;

  const paddingCondition =
    !insightsDataBeingChecked &&
    insightsDataCanBeLoaded &&
    ['genderAgeColumn', 'DOOHGenderAgeColumn'].includes(chartType);

  return (
    <div className={className} style={isHexColor(variant) && { borderTopColor: variant }}>
      <div className="reportingInsightsItem__containerTitle">
        <h3 className="reportingInsightsItem__title">
          {intl.formatMessage({
            defaultMessage: field,
            id: title
          })}
        </h3>
      </div>
      <hr
        style={{
          paddingBottom: paddingCondition ? 26 : 0
        }}
      />
      <ReportingInsightsRankingItem
        campaignInfo={campaignInfo}
        chartConfig={chartConfig}
        chartType={chartType}
        country={country}
        currencyInfo={currencyInfo}
        family={family}
        field={field}
        filterOptions={filterOptions}
        filters={filters}
        groupsOptions={groupsOptions}
        hideKpi={hideKpi}
        hideOthers={hideOthers}
        id={`insightsRankingItem_${id}`}
        insightsDataBeingChecked={insightsDataBeingChecked}
        insightsDataCanBeLoaded={insightsDataCanBeLoaded}
        insightsDataTotal={insightsDataTotal}
        itemId={itemId}
        metricsKpis={metricsKpis}
        onConfigChange={handleConfigChange}
        onFiltersChange={onFiltersChange}
        rankingConfig={rankingConfig}
        rawDataMode={rawDataMode}
        reportOptions={reportOptions}
        section={section}
        showMoreQuantity={showMoreQuantity}
        shownItems={shownItems}
      />
    </div>
  );
}

ReportingInsightsItem.propTypes = {
  campaignInfo: PropTypes.shape({
    cIds: PropTypes.arrayOf(PropTypes.string).isRequired,
    cartographyFilter: PropTypes.object,
    currencyOptions: PropTypes.shape({
      baseCurrency: PropTypes.string.isRequired,
      currency: PropTypes.string.isRequired
    }),
    dateInterval: PropTypes.shape({
      beginDate: PropTypes.string.isRequired,
      endDate: PropTypes.string.isRequired,
      timezone: PropTypes.string.isRequired
    }),
    userId: PropTypes.string.isRequired
  }),
  chartConfig: PropTypes.shape({
    colorAxis: PropTypes.object,
    colorByLabel: PropTypes.object,
    colors: PropTypes.arrayOf(PropTypes.string)
  }),
  chartType: PropTypes.oneOf([
    'stackedBar',
    'stackedColumn',
    'semiCircle',
    'pie',
    'genderAgeColumn',
    'DOOHGenderAgeColumn',
    'treemap'
  ]),
  containerSize: PropTypes.number.isRequired,
  country: PropTypes.string,
  currencyInfo: PropTypes.exact({
    baseCurrency: PropTypes.oneOf(Object.keys(BaseCurrencyEnum)),
    countries: PropTypes.arrayOf(PropTypes.string),
    currencyPosition: PropTypes.oneOf(Object.keys(BaseCurrencyPositionEnum)),
    currencySymbol: PropTypes.oneOf(Object.keys(BaseCurrencySymbolEnum))
  }),
  family: PropTypes.string,
  field: PropTypes.string.isRequired,
  filterOptions: PropTypes.shape({
    attrType: PropTypes.arrayOf(PropTypes.string)
  }),
  filters: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      props: PropTypes.any,
      type: PropTypes.oneOf([
        'selector',
        'checkbox',
        'boundary-shape',
        'content-category-provider',
        'dco-dimension-selector'
      ])
    })
  ),
  groupsOptions: PropTypes.arrayOf(
    PropTypes.shape({
      from: PropTypes.number,
      groupBy: PropTypes.string.isRequired,
      others: PropTypes.bool,
      size: PropTypes.number
    })
  ),
  hideKpi: PropTypes.bool,
  hideOthers: PropTypes.bool,
  id: PropTypes.string.isRequired,
  insightsDataBeingChecked: PropTypes.bool,
  insightsDataCanBeLoaded: PropTypes.bool,
  insightsDataTotal: PropTypes.number,
  itemId: PropTypes.string.isRequired,
  metricsKpis: PropTypes.shape({
    kpi: PropTypes.object,
    metric: PropTypes.object.isRequired
  }),
  onConfigChange: PropTypes.func,
  onFiltersChange: PropTypes.func,
  rankingConfig: PropTypes.shape({
    colorByLabel: PropTypes.object,
    colors: PropTypes.shape({
      items: PropTypes.arrayOf(PropTypes.string),
      others: PropTypes.string.isRequired
    }),
    flexDirection: PropTypes.oneOf(['row', 'column']),
    labelPosition: PropTypes.oneOf(['left', 'right']),
    showProgressBar: PropTypes.bool.isRequired,
    variant: PropTypes.string
  }),
  rawDataMode: PropTypes.shape({
    defaultActive: PropTypes.bool,
    disabled: PropTypes.bool,
    force: PropTypes.bool,
    hideCheckbox: PropTypes.bool,
    onChange: PropTypes.func
  }),
  reportOptions: PropTypes.object,
  section: PropTypes.string.isRequired,
  showMoreQuantity: PropTypes.number,
  shownItems: PropTypes.number.isRequired,
  title: PropTypes.string.isRequired,
  variant: PropTypes.string.isRequired
};

export default React.memo(ReportingInsightsItem);
